class PromotionInjector {
  'use strict';

  constructor() {
    if (PromotionInjector.instance) {
      return PromotionInjector.instance;
    }
    PromotionInjector.instance = this;

    this.gridSelector = '.boost-sd__filter-product-list .boost-sd__product-list';
    this.promotionsFetchUrl = '/pages/contact?view=ajax-grid-promotions';
    this.promotionItemSelector = '.el-product-grid-promotion';
    this.storageKey = 'el-product-grid-promotion-offset';

    this.promotions = [];
    this.promotionInsertOffset = 0;
    this.promotionGridOffset = 0;
    this.promotionsResponseHtml = null;
    this.windowWidth = window.innerWidth;
    this.currentCollectionHandle = window.collectionHandle || null;
    this.videoObserver = null;
    this.vimeoPlayers = new Map();

    this._observeGridAppearance();
    window.addEventListener('resize', this._debounce(this._onWindowResize.bind(this), 300));
  }

  _observeGridAppearance() {
    const gridContainer = document.querySelector('.boost-sd__filter-product-list');

    if (gridContainer) {
      this._init();
    } else {
      const observer = new MutationObserver((mutations, obs) => {
        const gridAppeared = document.querySelector('.boost-sd__filter-product-list');
        if (gridAppeared) {
          obs.disconnect();
          this._init();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  _init() {
    this.fetchGridPromotions();
    this._initGridObserver();
    this._initializeVideoObserver();
  }

  async fetchGridPromotions() {
    try {
      const response = await fetch(this.promotionsFetchUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      this.promotionsResponseHtml = await response.text();
      this.initialiseGridPromotions();
    } catch (error) {
      console.error('Failed to fetch grid promotions:', error);
    }
  }

  initialiseGridPromotions() {
    if (!this.promotionsResponseHtml) {
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.promotionsResponseHtml;

    const allPromotions = Array.from(tempDiv.querySelectorAll(this.promotionItemSelector));
    this.promotionsResponseHtml = null;

    if (allPromotions.length === 0) {
      return;
    }

    const specificPromotions = [];
    const generalPromotions = [];

    allPromotions.forEach((promoNode) => {
      const targetCollections = promoNode.dataset.collections;

      if (!targetCollections || targetCollections.length === 0) {
        generalPromotions.push(promoNode);
      } else {
        const targetHandles = targetCollections.split(',');
        if (this.currentCollectionHandle && targetHandles.includes(this.currentCollectionHandle)) {
          specificPromotions.push(promoNode);
        }
      }
    });

    const storedOffset = localStorage.getItem(this.storageKey);
    this.promotionGridOffset = storedOffset ? parseInt(storedOffset, 10) : 0;

    const rotatedSpecific = this._arrayRotate(specificPromotions, this.promotionGridOffset);
    const rotatedGeneral = this._arrayRotate(generalPromotions, this.promotionGridOffset);

    this.promotions = [...rotatedSpecific, ...rotatedGeneral];

    if (this.promotions.length === 0) {
      return;
    }

    this.populateGridWithPromotions();
  }

  populateGridWithPromotions() {
    if (!this.promotions || this.promotions.length === 0) {
      return;
    }

    const productGrid = document.querySelector(this.gridSelector);
    if (!productGrid || productGrid.children.length === 0) {
      setTimeout(() => this.populateGridWithPromotions(), 300);
      return;
    }

    const productGridChildren = Array.from(productGrid.children);
    const random = Math.random() < 0.5;
    let positions;
    const width = window.innerWidth;

    if (width >= 2720) {
      positions = random ? [6, 36, 54, 72] : [9, 32, 72, 97];
    } else if (width >= 2380) {
      positions = random ? [11, 21, 44, 75] : [12, 38, 56, 74];
    } else if (width >= 2040) {
      positions = random ? [14, 30, 48, 68] : [15, 31, 48, 69];
    } else if (width >= 1700) {
      positions = random ? [10, 31, 53, 71] : [11, 30, 53, 70];
    } else if (width >= 1360) {
      positions = random ? [13, 41, 58, 80] : [12, 41, 58, 81];
    } else if (width >= 768) {
      positions = random ? [12, 36, 72, 91] : [13, 37, 73, 90];
    } else {
      positions = random ? [19, 50, 87, 90] : [17, 46, 89, 95];
    }

    let insertedCount = 0;
    for (this.promotionInsertOffset; this.promotionInsertOffset < positions.length; this.promotionInsertOffset++) {
      if (this.promotions.length <= this.promotionInsertOffset) {
        break;
      }
      const positionIndex = positions[this.promotionInsertOffset];
      const gridItem = productGridChildren[positionIndex - this.promotionInsertOffset - 1];
      if (gridItem) {
        const promotionToInsert = this.promotions[this.promotionInsertOffset];
        gridItem.parentNode.insertBefore(promotionToInsert, gridItem);
        insertedCount++;
      } else {
        break;
      }
    }

    if (insertedCount > 0) {
      localStorage.setItem(this.storageKey, this.promotionGridOffset + 1);
    }

    this._scanAndObserveVideos();
  }

  _initGridObserver() {
    const productGrid = document.querySelector(this.gridSelector);
    if (!productGrid) {
      return;
    }
    const observer = new MutationObserver(
      this._debounce(() => {
        const promotionsInGrid = document.querySelectorAll(this.promotionItemSelector).length;
        if (promotionsInGrid === 0) {
          this.promotionInsertOffset = 0;
        }
        this.populateGridWithPromotions();
      }, 600),
    );
    observer.observe(productGrid, { childList: true });
  }

  _onWindowResize() {
    if (this.windowWidth !== window.innerWidth) {
      this.windowWidth = window.innerWidth;
      if (this.promotions && this.promotions.length > 0) {
        document.querySelectorAll(this.promotionItemSelector).forEach((el) => el.remove());
        this.promotionInsertOffset = 0;
        this.populateGridWithPromotions();
      }
    }
  }

  loadVimeoAPI() {
    return new Promise((resolve) => {
      elabs.DependencyService.fetchDependency('https://player.vimeo.com/api/player.js', resolve);
    });
  }

  async _initializeVideoObserver() {
    try {
      await this.loadVimeoAPI;
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const iframe = entry.target.querySelector('.el-product-grid-promotion__video');
            if (!iframe) {
              return;
            }

            const player = this.vimeoPlayers.get(iframe);
            if (!player) {
              return;
            }

            if (entry.isIntersecting) {
              iframe.dataset.shouldPlay = 'true';
              player.play().catch(() => {});
            } else {
              iframe.dataset.shouldPlay = 'false';
              player.pause();
            }
          });
        },
        { threshold: 0.1 },
      );
    } catch (error) {
      console.error('Failed to load Vimeo Player API', error);
    }
  }

  _scanAndObserveVideos() {
    if (!this.videoObserver) {
      setTimeout(() => this._scanAndObserveVideos(), 100);
      return;
    }

    const promotionItemsWithVideo = document.querySelectorAll(
      '.el-product-grid-promotion:has(.el-product-grid-promotion__video[src*="vimeo.com"])',
    );

    promotionItemsWithVideo.forEach((promoItem) => {
      const iframe = promoItem.querySelector('.el-product-grid-promotion__video');
      if (!iframe) {
        return;
      }

      if (!this.vimeoPlayers.has(iframe)) {
        const player = new Vimeo.Player(iframe);
        this.vimeoPlayers.set(iframe, player);

        player
          .ready()
          .then(() => {
            player.setVolume(0);
            player.setLoop(true);

            player.on('pause', () => {
              if (iframe.dataset.shouldPlay === 'true') {
                player.play().catch(() => {});
              }
            });

            this.videoObserver.observe(promoItem);
          })
          .catch((error) => {
            console.error('Vimeo player not ready:', error);
          });
      }
    });
  }

  _debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  _arrayRotate(arr, count) {
    if (!arr || arr.length === 0) {
      return [];
    }
    count -= arr.length * Math.floor(count / arr.length);
    arr.push.apply(arr, arr.splice(0, count));
    return arr;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  void new PromotionInjector();
});
